import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Home, Mail, MessageCircle, Clock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { safeSetItem } from "@/lib/safe-storage";
import ComplianceFooter from "@/components/ComplianceFooter";

const Support = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Support - Ascendry";
    
    // Check authentication status
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setIsAuthenticated(!!session);
    };
    
    checkAuth();
  }, []);

  const handleTicketSystemClick = () => {
    if (isAuthenticated === null) return; // Still loading
    
    if (isAuthenticated) {
      navigate('/support/tickets');
    } else {
      // Store the intended destination and redirect to login
      safeSetItem('redirectAfterLogin', '/support/tickets');
      navigate('/auth/student');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-dark">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Back to Home Button */}
        <div className="mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center space-x-2 text-muted-foreground hover:text-primary transition-colors"
          >
            <Home className="w-4 h-4" />
            Back to Home
          </Link>
        </div>

        <h1 className="text-4xl font-bold mb-8 text-center text-glow">Support</h1>
        
        <div className="space-y-8">
          {/* Main Support Card */}
          <Card className="obsidian-card">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2 text-energy">
                <MessageCircle className="w-6 h-6" />
                <span>Get Help & Support</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-lg text-muted-foreground">
                For any and all support inquiries, technical assistance, or questions about Ascendry, 
                our team is here to help you succeed.
              </p>

              <div className="bg-primary/10 rounded-lg p-6 space-y-4">
                <h2 className="text-xl font-semibold text-energy">Direct Support Contact</h2>
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-primary" />
                  <div>
                    <p className="text-muted-foreground">Email us directly for personalized support:</p>
                    <a 
                      href="mailto:Lance@Blaqout.us" 
                      className="text-primary hover:text-energy transition-colors font-semibold text-lg"
                    >
                      Lance@Blaqout.us
                    </a>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <h3 className="font-semibold text-energy">What we can help with:</h3>
                  <ul className="space-y-1 text-muted-foreground">
                    <li>• Account setup and management</li>
                    <li>• Session booking assistance</li>
                    <li>• Payment and credit issues</li>
                    <li>• Technical troubleshooting</li>
                    <li>• Mentor application support</li>
                    <li>• Zoom integration help</li>
                  </ul>
                </div>
                
                <div className="space-y-2">
                  <h3 className="font-semibold text-energy">Response Times:</h3>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Clock className="w-4 h-4 text-primary" />
                      <span className="text-muted-foreground">General inquiries: 24-48 hours</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Clock className="w-4 h-4 text-primary" />
                      <span className="text-muted-foreground">Technical issues: 12-24 hours</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Clock className="w-4 h-4 text-primary" />
                      <span className="text-muted-foreground">Urgent matters: Same day</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Tips Card */}
          <Card className="obsidian-card">
            <CardHeader>
              <CardTitle className="text-energy">Before You Reach Out</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                To help us assist you more quickly, please include the following information in your email:
              </p>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Your account email address</li>
                <li>• A detailed description of the issue</li>
                <li>• Screenshots if applicable</li>
                <li>• Steps you've already tried</li>
                <li>• Your browser and device information (for technical issues)</li>
              </ul>
            </CardContent>
          </Card>

          {/* Contact Buttons */}
          <div className="text-center space-y-4">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={handleTicketSystemClick} 
                className="energy-button"
                disabled={isAuthenticated === null}
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                {isAuthenticated === null ? "Loading..." : "Support Ticket System"}
              </Button>
              <Button asChild variant="outline">
                <a href="mailto:Lance@Blaqout.us?subject=Ascendry Support Request">
                  <Mail className="w-4 h-4 mr-2" />
                  Send Direct Email
                </a>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Use our ticket system for organized support, or email for immediate needs
            </p>
            <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
              <p className="text-sm text-yellow-200">
                <strong>Mentors:</strong> If you need support, please use the{' '}
                <Link to="/auth/mentor" className="text-yellow-400 hover:text-yellow-300 underline">
                  mentor login
                </Link>{' '}
                to access your support tickets, or contact admin directly via email.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <ComplianceFooter />
    </div>
  );
};

export default Support;